import { useState } from 'react';

import { fullTimestamp, messageTime } from '../format';

import { MediaMessageContent } from './MediaMessageContent';
import { MessageAttachment } from './MessageAttachment';
import { MessageDeliveryStatusIndicator } from './MessageDeliveryStatus';
import { MessageFailedModal } from './MessageFailedModal';

import type { MessageItem } from '../../../lib/types';
import type { CSSProperties } from 'react';

/**
 * MessageBubble — bolha de mensagem inbound/outbound.
 *
 * Variantes:
 *   - inbound (cliente)  → alinhada à esquerda, fundo neutro
 *   - outbound (atendente) → alinhada à direita, fundo accent
 *   - outbound (ai)        → idem direita, fundo var(--ai-bg) DS token
 *
 * Status de entrega (Fatia 6 PR-A + r7b §30 ADR-113):
 *   queued (⏸) · sending (⏱) · sent (✓) · delivered (✓✓) · read (✓✓ azul)
 *   failed (⚠ + modal) · retrying (↻ N/M)
 *
 * `MessageOrigin === 'system'` é renderizado por `SystemEventCard` (não
 * por essa Bubble) — vide `MessageList`.
 *
 * Fase 3.A DS Foundation cascade (Item 158):
 *   Adopta .msg/.msg--inbound/.msg--outbound/.msg__bubble/.msg__meta do DS
 *   @2.2.0 (components.css §§ 1273-1305). Tailwind preservado para layout
 *   de alinhamento externo (justify-start/end my-1) onde DS não cobre.
 *   AI bubble: .msg--outbound base + inline override var(--ai-bg/fg/border)
 *   via DS tokens (DS não tem variante .msg--from-ai dedicada).
 */
export function MessageBubble({
  message,
  onRetry,
}: {
  message: MessageItem;
  onRetry?: (optimisticId: string) => void;
}): JSX.Element {
  const [failedModalOpen, setFailedModalOpen] = useState(false);
  const isInbound = message.direction === 'inbound';
  const isAi = message.origin === 'ai';

  // Respostas Prontas do Operador S2 (Task 7) — bubble de mídia outbound
  // (contentType image/video/audio + metadata.mediaAssetId gravado pelo
  // burst de uma resposta pronta). Sem mediaAssetId → degradação honesta
  // para o render de texto normal (nunca quebra a bolha).
  const mediaKind =
    (message.contentType === 'image' ||
      message.contentType === 'video' ||
      message.contentType === 'audio') &&
    typeof message.metadata?.mediaAssetId === 'string'
      ? message.contentType
      : null;
  const mediaAssetId = mediaKind !== null ? (message.metadata.mediaAssetId as string) : null;

  // DS canonical: .msg--inbound / .msg--outbound
  const msgDirectionClass = isInbound ? 'msg--inbound' : 'msg--outbound';
  // External row alignment — DS .msg handles internal column layout; Tailwind handles row
  const containerAlign = isInbound ? 'justify-start' : 'justify-end';

  // AI bubble: override DS .msg--outbound (brand-subtle) with DS var(--ai-bg) tokens
  const aiBubbleStyle = isAi
    ? ({
        background: 'var(--ai-bg)',
        color: 'var(--ai-fg)',
        borderColor: 'var(--ai-border)',
      } as CSSProperties)
    : undefined;

  return (
    <>
      <div className={`flex ${containerAlign} my-1`}>
        {/* DS canonical .msg shell — replaces max-w-[70%] flex flex-col gap-1 */}
        <div className={`msg ${msgDirectionClass}`}>
          {/* DS canonical .msg__bubble — replaces bubbleTone + bubbleRadius Tailwind */}
          <div className="msg__bubble" style={aiBubbleStyle}>
            {isAi && (
              /* AI label badge — no dedicated DS class; uses DS tokens via inline style (r7b pattern) */
              <span
                className="font-mono text-2xs uppercase tracking-wide font-medium mr-2"
                style={{
                  background: 'var(--ai-bg)',
                  color: 'var(--ai-fg)',
                  border: '1px solid var(--ai-border)',
                  padding: '0 4px',
                  borderRadius: 'var(--radius-pill)',
                }}
              >
                IA
              </span>
            )}
            {/* Item 36 (PR-B2) — render anexo quando contentType=document. */}
            {message.contentType === 'document' && message.attachedDocument !== undefined ? (
              <>
                <MessageAttachment attached={message.attachedDocument} />
                {message.content.length > 0 && (
                  <span
                    className="whitespace-pre-wrap break-words"
                    style={{ display: 'block', marginTop: 4, fontSize: 13 }}
                  >
                    {message.content}
                  </span>
                )}
              </>
            ) : mediaKind !== null && mediaAssetId !== null ? (
              <MediaMessageContent
                kind={mediaKind}
                mediaAssetId={mediaAssetId}
                caption={message.content}
              />
            ) : (
              <span className="whitespace-pre-wrap break-words">{message.content}</span>
            )}
          </div>
          {/* DS canonical .msg__meta — replaces flex gap-1 text-2xs text-text-secondary */}
          <div className={`msg__meta ${isInbound ? '' : 'justify-end'}`}>
            {/* Hora absoluta acima de 1min (decisão do TL, 13/Ago): o relativo
                apagava o INTERVALO entre as falas, que é o dado operacional do
                fio. O `title` guarda a data+hora completas. */}
            <time dateTime={message.sentAt} title={fullTimestamp(message.sentAt)}>
              {messageTime(message.sentAt)}
            </time>
            {!isInbound && (
              <MessageDeliveryStatusIndicator
                status={message.status}
                failureReason={message.failureReason}
                retryAttempt={message.retryAttempt ?? null}
                retryMax={message.retryMax ?? null}
                {...(message.status === 'failed' && {
                  onDetails: () => setFailedModalOpen(true),
                })}
                {...(onRetry !== undefined &&
                  message.status === 'failed' && {
                    onRetry: () => onRetry(message.id),
                  })}
              />
            )}
          </div>
        </div>
      </div>

      {/* r7b §30.2 — Modal de detalhes para mensagem com falha */}
      {message.status === 'failed' && (
        <MessageFailedModal
          open={failedModalOpen}
          message={message}
          onClose={() => setFailedModalOpen(false)}
          {...(onRetry !== undefined && {
            onRetry: (_msg: typeof message) => {
              setFailedModalOpen(false);
              onRetry(_msg.id);
            },
          })}
        />
      )}
    </>
  );
}
